import React, { useRef, useState } from 'react'
import { useMutation, gql } from '@apollo/client'
import { validateFunc } from '../../constraints/constraints'
import { withTranslation } from 'react-i18next'
import { createVendor, editVendor, getVendors } from '../../apollo'
import {
  Input,
  Button,
  Alert,
  Box,
  Typography,
  IconButton
} from '@mui/material'
import useStyles from './styles'
import useGlobalStyles from '../../utils/globalStyles'
import FormCardHeader from '../FormCardHeader'
import InputAdornment from '@mui/material/InputAdornment'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'

const CREATE_VENDOR = gql`
  ${createVendor}
`
const EDIT_VENDOR = gql`
  ${editVendor}
`
const GET_VENDORS = gql`
  ${getVendors}
`

function Vendor(props) {
  const formRef = useRef()
  const [showPassword, setShowPassword] = useState(false)
  const mutation = props.vendor ? EDIT_VENDOR : CREATE_VENDOR
  const email = props.vendor ? props.vendor.email : ''
  const [error, errorSetter] = useState('')
  const [success, successSetter] = useState('')
  const [emailError, emailErrorSetter] = useState(null)
  const [passError, passErrorSetter] = useState(null)
  const { t } = props
  const onCompleted = data => {
    if (!props.vendor) clearFields()
    const message = props.vendor
      ? t('VendorUpdatedSuccessfully')
      : t('VendorAddedSuccessfully')
    errorSetter('')
    successSetter(message)
    setTimeout(hideAlert, 3000)
  }

  const onError = ({ graphQLErrors, networkError }) => {
    successSetter('')
    if (graphQLErrors) errorSetter(graphQLErrors[0].message)
    else if (networkError) errorSetter(networkError.result.errors[0].message)
    else errorSetter('Something went wrong!')
    setTimeout(hideAlert, 3000)
  }
  const [mutate, { loading: mutateLoading }] = useMutation(mutation, {
    refetchQueries: [{ query: GET_VENDORS }],
    onError,
    onCompleted
  })

  const onBlur = (setter, field, state) => {
    setter(!validateFunc({ [field]: state }, field))
  }

  const onSubmitValidaiton = () => {
    const emailError = !validateFunc(
      { email: formRef.current['input-email'].value },
      'email'
    )
    const passwordError = props.vendor
      ? true
      : !validateFunc(
          { password: formRef.current['input-password'].value },
          'password'
        )

    emailErrorSetter(emailError)
    passErrorSetter(passwordError)
    return emailError && passwordError
  }
  const clearFields = () => {
    formRef.current.reset()
    emailErrorSetter('')
    passErrorSetter('')
  }

  const hideAlert = () => {
    errorSetter('')
    successSetter('')
  }

  const classes = useStyles()
  const globalClasses = useGlobalStyles()
  const isEdit = Boolean(props.vendor)
  const required = (
    <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
      *
    </Box>
  )
  const onSave = e => {
    e.preventDefault()
    if (onSubmitValidaiton() && !mutateLoading) {
      mutate({
        variables: {
          vendorInput: {
            _id: props.vendor ? props.vendor._id : '',
            email: formRef.current['input-email'].value.toLowerCase(),
            password: formRef.current['input-password']
              ? formRef.current['input-password'].value
              : ''
          }
        }
      })
      // Close the edit modal once the result message has been seen
      setTimeout(() => {
        if (typeof props.onClose === 'function') {
          props.onClose()
        }
      }, 4000)
    }
  }

  return (
    <Box className={classes.container}>
      <FormCardHeader
        className={classes.heading}
        icon={StorefrontOutlinedIcon}
        title={isEdit ? t('EditVendor') : t('Add vendor')}
        subtitle={isEdit ? null : t('Create a vendor account.')}
        onClose={props.onClose}
      />
      <Box className={classes.form}>
        <form ref={formRef} onSubmit={onSave}>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              alignItems: 'end',
              gridTemplateColumns: {
                xs: '1fr',
                md: isEdit ? '1fr' : '1fr 1fr auto'
              }
            }}>
            <Box>
              <Typography className={classes.labelText}>
                {t('Email address')}
                {required}
              </Typography>
              <Input
                id="input-email"
                name="input-email"
                placeholder={t('Enter vendor email')}
                type="email"
                disableUnderline
                className={[
                  globalClasses.input,
                  emailError === false
                    ? globalClasses.inputError
                    : emailError === true
                    ? globalClasses.inputSuccess
                    : ''
                ]}
                defaultValue={email}
                onBlur={event =>
                  onBlur(emailErrorSetter, 'email', event.target.value)
                }
              />
            </Box>
            {!isEdit ? (
              <Box>
                <Typography className={classes.labelText}>
                  {t('Password')}
                  {required}
                </Typography>
                <Input
                  placeholder={t('Enter password')}
                  disableUnderline
                  className={[
                    globalClasses.input,
                    passError === false
                      ? globalClasses.inputError
                      : passError === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                  id="input-password"
                  name="input-password"
                  type={showPassword ? 'text' : 'password'}
                  onBlur={event => {
                    onBlur(passErrorSetter, 'password', event.target.value)
                  }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        aria-label={t('Toggle password visibility')}
                        onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? (
                          <VisibilityIcon fontSize="small" />
                        ) : (
                          <VisibilityOffIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Box>
            ) : null}
            <Button
              type="submit"
              className={globalClasses.button}
              sx={{ m: '0 !important', px: '36px !important' }}
              disabled={mutateLoading}>
              {isEdit ? t('Update') : t('Save vendor')}
            </Button>
          </Box>
        </form>
        {success || error ? (
          <Box mt={2}>
            {success && (
              <Alert
                className={globalClasses.alertSuccess}
                variant="filled"
                severity="success">
                {success}
              </Alert>
            )}
            {error && (
              <Alert
                className={globalClasses.alertError}
                variant="filled"
                severity="error">
                {error}
              </Alert>
            )}
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}
export default withTranslation()(Vendor)
